import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Descriptions,
  Space,
  Button,
  Divider,
  Typography,
  Alert,
  Select,
  Tag,
  Table,
} from 'antd';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { decodeJwtClaims, readStorage, redact, normalizeBase, readCookie } from '../utils';

const { Title, Text } = Typography;

type CapResult = { feature: string; action: string; allowed: boolean };

export default function AuthDiagnosticsPanel({
  idpBase,
  apiBase,
}: {
  idpBase: string;
  apiBase: string;
}) {
  // Auth & token diagnostics
  const isAuthenticated = useGlobalStore((s: GlobalState) => s.isAuthenticated);
  const accessToken = useGlobalStore((s: GlobalState) => s.token);
  const refreshToken = useGlobalStore((s: GlobalState) => s.refreshToken);
  const jti = useGlobalStore((s: GlobalState) => s.jti);
  const doLogout = useGlobalStore((s: GlobalState) => s.logout);

  const storageAccess = useMemo(() => readStorage('accessToken'), []);
  const storageRefresh = useMemo(() => readStorage('refreshToken'), []);
  const storageJti = useMemo(() => readStorage('jti'), []);

  const claims = useMemo(() => decodeJwtClaims(accessToken), [accessToken]);
  const aud = Array.isArray(claims?.['aud'])
    ? (claims?.['aud'] as string[]).join(',')
    : (claims?.['aud'] as string | undefined);
  const iss = (claims?.['iss'] as string) || undefined;
  const sub = (claims?.['sub'] as string) || undefined;
  const email = (claims?.['email'] as string) || (claims?.['upn'] as string) || undefined;
  const exp = typeof claims?.['exp'] === 'number' ? (claims?.['exp'] as number) : undefined;
  const iat = typeof claims?.['iat'] === 'number' ? (claims?.['iat'] as number) : undefined;
  const roles = (claims?.['roles'] as string[]) || (claims?.['role'] as string[]) || undefined;
  const tenants =
    (claims?.['tenantIds'] as string[]) ||
    (claims?.['tenants'] as string[]) ||
    (claims?.['tenant_id'] as string) ||
    (claims?.['tenantId'] as string) ||
    undefined;

  const nowSec = Math.floor(Date.now() / 1000);
  const ttlSec = exp ? Math.max(0, exp - nowSec) : undefined;
  const expHuman = exp ? new Date(exp * 1000).toLocaleString() : undefined;
  const iatHuman = iat ? new Date(iat * 1000).toLocaleString() : undefined;

  // IDP profile fetch ("who am I")
  type IdpProfile = { id?: string; email?: string; status?: string } | null;
  const [profile, setProfile] = useState<IdpProfile>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const fetchProfile = useCallback(async () => {
    setProfileError(null);
    setProfile(null);
    if (!accessToken) return;
    try {
      const base = normalizeBase(idpBase);
      const csrfToken = readCookie('XSRF-TOKEN');
      const res = await fetch(`${base}/api/auth/profile`, {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          ...(csrfToken ? { 'x-xsrf-token': csrfToken } : {}),
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = (await res.json()) as unknown;
      // Tolerate multiple profile shapes:
      // - { id, email, status }
      // - { userId, email, active }
      // - { user: { id|userId, email, active|status } }
      const get = (o: unknown, k: string): unknown =>
        o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined;
      const user = get(raw, 'user');
      const idVal =
        (get(raw, 'id') as string | undefined) ||
        (get(raw, 'userId') as string | undefined) ||
        (get(user, 'id') as string | undefined) ||
        (get(user, 'userId') as string | undefined) ||
        (get(raw, 'sub') as string | undefined) ||
        (get(user, 'sub') as string | undefined);
      const emailVal =
        (get(raw, 'email') as string | undefined) ||
        (get(user, 'email') as string | undefined) ||
        email ||
        undefined;
      const activeRaw =
        (get(raw, 'active') as boolean | undefined) || (get(user, 'active') as boolean | undefined);
      const statusRaw =
        (get(raw, 'status') as string | undefined) || (get(user, 'status') as string | undefined);
      const statusVal =
        statusRaw ||
        (typeof activeRaw === 'boolean' ? (activeRaw ? 'ACTIVE' : 'INACTIVE') : undefined);
      setProfile({ id: idVal, email: emailVal, status: statusVal });
    } catch (e) {
      setProfileError((e as Error)?.message || 'Failed to load profile');
    }
  }, [idpBase, accessToken, email]);
  useEffect(() => {
    if (isAuthenticated) void fetchProfile();
  }, [isAuthenticated, fetchProfile]);

  // Capabilities quick check
  type CapCheck = { feature: string; action: string };
  const checks: CapCheck[] = useMemo(
    () => [
      { feature: 'TENANT_MGMT', action: 'read' },
      { feature: 'USER_MGMT', action: 'read' },
      { feature: 'PERMISSION_ASSIGNMENT', action: 'ASSIGN' },
      { feature: 'SESSION_MGMT', action: 'REVOKE' },
    ],
    [],
  );
  const tenantIdsFromClaims = useMemo(() => {
    if (!claims) return [] as string[];
    if (Array.isArray(claims['tenantIds'])) return claims['tenantIds'] as string[];
    const single =
      (claims['tenant_id'] as string) ||
      (claims['tenantId'] as string) ||
      (claims['tid'] as string);
    return single ? [single] : [];
  }, [claims]);
  const [tenantId, setTenantId] = useState<string | undefined>(() => tenantIdsFromClaims[0]);
  useEffect(() => {
    setTenantId((prev) => prev ?? tenantIdsFromClaims[0]);
  }, [tenantIdsFromClaims]);
  const [capLoading, setCapLoading] = useState(false);
  const [capError, setCapError] = useState<string | null>(null);
  type CapResultWithReason = CapResult & { reason?: 'explicit' | 'bypass' | 'none' };
  const [capResults, setCapResults] = useState<CapResultWithReason[] | null>(null);
  const runCaps = useCallback(async () => {
    setCapError(null);
    setCapResults(null);
    if (!accessToken) return;
    try {
      setCapLoading(true);
      const base = normalizeBase(apiBase);
      const res = await fetch(`${base}/api/capabilities/can`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
        },
        body: JSON.stringify({ checks }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { results?: CapResultWithReason[] };
      setCapResults((data.results ?? []) as CapResultWithReason[]);
    } catch (e) {
      setCapError((e as Error)?.message || 'Failed to load capabilities');
    } finally {
      setCapLoading(false);
    }
  }, [apiBase, accessToken, tenantId, checks]);
  useEffect(() => {
    if (isAuthenticated) void runCaps();
  }, [isAuthenticated, runCaps]);

  // Load tenant names for selector
  type TenantLite = { id: string; name: string };
  const [tenantsList, setTenantsList] = useState<TenantLite[]>([]);
  const [tenantsErr, setTenantsErr] = useState<string | null>(null);
  const loadTenants = useCallback(async () => {
    setTenantsErr(null);
    setTenantsList([]);
    if (!accessToken) return;
    try {
      const base = normalizeBase(apiBase);
      const res = await fetch(`${base}/api/tenants?skip=0&take=100`, {
        headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as Array<{ id: string; name: string }>;
      setTenantsList(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = (e as Error)?.message || 'Failed to load tenants';
      setTenantsErr(msg);
      // Fallback: try to resolve each claim tenant by id using x-tenant-id scoped calls
      try {
        const ids = tenantIdsFromClaims;
        if (!ids.length) return;
        const base = normalizeBase(apiBase);
        const resolved: TenantLite[] = [];
        for (const id of ids) {
          try {
            const r = await fetch(`${base}/api/tenants/${id}`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json',
                'x-tenant-id': id,
              },
            });
            if (r.ok) {
              const t = (await r.json()) as { id: string; name?: string };
              resolved.push({ id, name: t?.name || id });
            } else {
              resolved.push({ id, name: id });
            }
          } catch {
            resolved.push({ id, name: id });
          }
        }
        if (resolved.length) setTenantsList(resolved);
      } catch {
        // ignore fallback errors
      }
    }
  }, [apiBase, accessToken, tenantIdsFromClaims]);
  useEffect(() => {
    if (isAuthenticated) void loadTenants();
  }, [isAuthenticated, loadTenants]);
  const tenantOptions = useMemo(() => {
    const opts = tenantsList.map((t) => ({ label: t.name, value: t.id }));
    if (opts.length === 0) return tenantIdsFromClaims.map((t) => ({ label: t, value: t }));
    return opts;
  }, [tenantsList, tenantIdsFromClaims]);
  const selectedTenantName = useMemo(() => {
    if (!tenantId) return 'none';
    const found = tenantsList.find((t) => t.id === tenantId);
    return found?.name ?? tenantId;
  }, [tenantId, tenantsList]);

  return (
    <Card title="Auth & Storage Diagnostics" style={{ marginTop: 16 }}>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Authenticated">{String(isAuthenticated)}</Descriptions.Item>
        <Descriptions.Item label="Access token (store)">{redact(accessToken)}</Descriptions.Item>
        <Descriptions.Item label="Refresh token (store)">{redact(refreshToken)}</Descriptions.Item>
        <Descriptions.Item label="JTI (store)">{jti || '—'}</Descriptions.Item>
        <Descriptions.Item label="Access token (storage)">
          {storageAccess.where} {storageAccess.value ? `(${redact(storageAccess.value)})` : ''}
        </Descriptions.Item>
        <Descriptions.Item label="Refresh token (storage)">
          {storageRefresh.where} {storageRefresh.value ? `(${redact(storageRefresh.value)})` : ''}
        </Descriptions.Item>
        <Descriptions.Item label="JTI (storage)">
          {storageJti.where} {storageJti.value ? `(${redact(storageJti.value)})` : ''}
        </Descriptions.Item>
        <Descriptions.Item label="Audience (JWT)">{aud || '—'}</Descriptions.Item>
        <Descriptions.Item label="Issuer (iss)">{iss || '—'}</Descriptions.Item>
        <Descriptions.Item label="Subject (sub)">{sub || '—'}</Descriptions.Item>
        <Descriptions.Item label="Email (claim)">{email || '—'}</Descriptions.Item>
        <Descriptions.Item label="Roles (claim)">
          {Array.isArray(roles) ? roles.join(', ') : roles || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Tenant(s) (claim)">
          {(() => {
            const ids = Array.isArray(tenants) ? tenants : tenants ? [tenants] : [];
            if (!ids.length) return '—';
            // Resolve names from tenantsList
            const display = ids.map((id) => {
              const found = tenantsList.find((t) => t.id === id);
              return found?.name || id;
            });
            return display.join(', ');
          })()}
        </Descriptions.Item>
        <Descriptions.Item label="Issued at (iat)">{iatHuman || '—'}</Descriptions.Item>
        <Descriptions.Item label="Expires at (exp)">{expHuman || '—'}</Descriptions.Item>
        <Descriptions.Item label="Time to expiry">
          {ttlSec !== undefined ? `${ttlSec}s` : '—'}
        </Descriptions.Item>
      </Descriptions>

      <Space style={{ marginTop: 12 }}>
        <Button danger onClick={() => doLogout()}>
          Clear tokens (logout)
        </Button>
        <Button onClick={() => fetchProfile()} disabled={!isAuthenticated || idpBase == null}>
          Refresh profile
        </Button>
      </Space>

      <Divider style={{ margin: '16px 0' }} />
      <Space orientation="vertical" style={{ width: '100%' }}>
        <Title level={5} style={{ margin: 0 }}>
          IDP Profile
        </Title>
        {profileError ? (
          <Alert type="warning" title={`Profile: ${profileError}`} />
        ) : profile ? (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="User ID">{profile?.id || '—'}</Descriptions.Item>
            <Descriptions.Item label="Email">{profile?.email || '—'}</Descriptions.Item>
            <Descriptions.Item label="Status">{profile?.status || '—'}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Text type="secondary">{isAuthenticated ? 'Loading profile…' : 'Not authenticated'}</Text>
        )}
      </Space>

      <Divider style={{ margin: '16px 0' }} />
      <Space orientation="vertical" style={{ width: '100%' }}>
        <Space align="center">
          <Title level={5} style={{ margin: 0 }}>
            Capabilities (sample)
          </Title>
          {tenantId ? <Tag color="blue">Tenant: {selectedTenantName}</Tag> : null}
        </Space>
        <Text type="secondary">
          Legend: <Tag color="green">Allowed</Tag> = explicit permission;{' '}
          <Tag color="green">Allowed (bypass)</Tag> = platform/AC admin bypass; <Tag>Denied</Tag> =
          no explicit permission.
        </Text>
        <Space>
          <Text type="secondary">Tenant</Text>
          <Select
            size="small"
            style={{ minWidth: 260 }}
            value={tenantId}
            placeholder="none"
            allowClear
            onChange={(v) => setTenantId(v)}
            options={tenantOptions}
          />
          {tenantsErr ? <Alert type="warning" title={`Tenants: ${tenantsErr}`} /> : null}
          <Button onClick={() => runCaps()} loading={capLoading} disabled={!isAuthenticated}>
            Re-run
          </Button>
        </Space>
        {capError && <Alert type="warning" title={`Capabilities: ${capError}`} />}
        {capResults && (
          <Table
            size="small"
            pagination={false}
            rowKey={(r: CapResult) => `${r.feature}:${r.action}`}
            columns={[
              { title: 'Feature', dataIndex: 'feature', key: 'feature' },
              { title: 'Action', dataIndex: 'action', key: 'action' },
              {
                title: 'Allowed',
                key: 'allowed',
                render: (_: unknown, r: CapResultWithReason) =>
                  r.allowed ? (
                    <Tag color="green">
                      {r.reason === 'bypass' ? 'Allowed (bypass)' : 'Allowed'}
                    </Tag>
                  ) : (
                    <Tag>Denied</Tag>
                  ),
              },
            ]}
            dataSource={capResults}
          />
        )}
      </Space>
    </Card>
  );
}
